import React, { useCallback, useRef } from "react";
import { Typography, TextField } from "@mui/material";
import { scrollIntoViewAfterKeyboard } from "@/hooks/useScrollIntoViewOnFocus";

interface LabeledValueProps {
  value?: number | string;
  endAdornment: string;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => void;
  index: number;
  handleBlur: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => void;
  maxValue?: number;
  hasError: boolean;
  handleFocus: (index: number) => void;
  inputType?: "numeric" | "time";
}

const getInputProps = (inputType?: "numeric" | "time", maxValue?: number) => {
  if (inputType === "time") {
    return {
      pattern: "[0-9]{1,2}:[0-5][0-9]:[0-5][0-9]",
      placeholder: "",
    };
  }
  return {
    inputMode: "numeric",
    max: maxValue,
    pattern: "[0-9]*",
  };
};

const formatTimeInput = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/[^0-9]/g, "");

  // Limit to 6 digits (HHMMSS)
  const limitedNumbers = numbers.slice(0, 6);

  // Apply formatting based on length
  if (limitedNumbers.length <= 2) {
    return limitedNumbers;
  } else if (limitedNumbers.length <= 4) {
    return `${limitedNumbers.slice(0, 2)}:${limitedNumbers.slice(2)}`;
  } else {
    return `${limitedNumbers.slice(0, 2)}:${limitedNumbers.slice(2, 4)}:${limitedNumbers.slice(4)}`;
  }
};

const getDisplayValue = (value?: number | string) => {
  if (value === null || value === undefined) {
    return "";
  }
  return value.toString();
};

const FormTextField: React.FC<LabeledValueProps> = ({
  value,
  endAdornment,
  onChange,
  index,
  handleBlur,
  maxValue,
  hasError,
  handleFocus,
  inputType = "numeric",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const onFocus = useCallback(() => {
    handleFocus(index);
    scrollIntoViewAfterKeyboard(inputRef.current);
  }, [handleFocus, index]);

  return (
    <TextField
      value={getDisplayValue(value)}
      variant="outlined"
      type={inputType === "time" ? "text" : "number"}
      error={hasError}
      inputRef={inputRef}
      onChange={(event) => {
        if (inputType === "time") {
          // Apply time formatting and create a new event with formatted value
          const formattedValue = formatTimeInput(event.target.value);
          const syntheticEvent = {
            ...event,
            target: {
              ...event.target,
              value: formattedValue,
            },
          };
          onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>, index);
        } else {
          onChange(event, index);
        }
      }}
      onBlur={(event) => handleBlur(event, index)}
      onFocus={onFocus}
      slotProps={{
        htmlInput: getInputProps(inputType, maxValue),
        input: {
          endAdornment: (
            <Typography color="text.secondary" variant="prose-3xs">
              {endAdornment}
            </Typography>
          ),
        },
      }}
      sx={{
        "& .MuiInputBase-root": {
          width: inputType === "time" ? "120px" : "100px",
          height: "40px",
          backgroundColor: "transparent",
          color: "text.secondary",
        },
      }}
    />
  );
};

export default FormTextField;
